import CoinIcon from '../components/CoinIcon';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import { FiArrowLeft, FiMapPin, FiMail, FiSend, FiMessageCircle, FiImage, FiStar, FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import LoadingScreen from '../components/LoadingScreen';

export default function VendorProfile() {
  const { id } = useParams();
  const { user, showToast } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [events, setEvents] = useState([]);
  const [bookingData, setBookingData] = useState({ event_id: '', message: '' });
  const [lightbox, setLightbox] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date());
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, review_text: '', event_type: '' });
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    api.get(`/vendors/${id}`).then(r => setVendor(r.data)).catch(() => setFetchError(true));
    api.get(`/vendors/${id}/portfolio`).then(r => setPortfolio(r.data)).catch(() => {});
    api.get(`/vendors/${id}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    api.get(`/vendors/${id}/schedule`).then(r => setSchedule(r.data)).catch(() => {});
    if (user) api.get('/events').then(r => setEvents(r.data)).catch(() => {});
  }, [id, user]);

  // All hooks must be called before any early return (React hooks rules)
  const [heroIdx, setHeroIdx] = useState(0);
  const heroIntervalRef = useRef(null);
  const heroPausedRef = useRef(false);

  // Compute hero media from portfolio or legacy gallery (safe with null vendor)
  const heroMedia = (vendor && portfolio.length > 0)
    ? portfolio.filter(p => p.image_url)
    : ((vendor && vendor.business_gallery) || []).map(url => ({ image_url: url, media_type: 'image' }));

  useEffect(() => {
    if (heroMedia.length <= 1) return;
    heroIntervalRef.current = setInterval(() => {
      if (!heroPausedRef.current) {
        setHeroIdx(i => (i + 1) % heroMedia.length);
      }
    }, 3000);
    return () => clearInterval(heroIntervalRef.current);
  }, [heroMedia.length]);

  const handleImgError = (e) => {
    e.target.style.display = 'none';
  };

  if (fetchError) {
    return (
      <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h3>Vendor not found</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          This vendor profile doesn't exist or may have been removed.
        </p>
        <Link to="/vendors" className="btn btn-primary">Browse Vendors</Link>
      </div>
    );
  }

  if (!vendor) return <LoadingScreen />;

  const sendInquiry = async () => {
    try {
      await api.post(`/vendors/${id}/book`, bookingData);
      showToast('Booking inquiry sent!');
      setShowBooking(false);
    } catch { showToast('Failed to send inquiry', 'error'); }
  };

  const startChat = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const eventId = events?.[0]?.id || null;
      const r = await api.post('/chat/conversations', {
        vendor_id: parseInt(id),
        subject: `Inquiry about ${vendor.business_name}`,
        event_id: eventId
      });
      if (eventId) {
        navigate(`/event/${eventId}?tab=vendors&convid=${r.data.id}`);
      } else {
        showToast('Chat started! Create an event to chat with vendors.');
        navigate('/dashboard');
      }
    } catch { showToast('Failed to start chat', 'error'); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/vendors/${id}/reviews`, reviewData);
      showToast('Review submitted!');
      setShowReviewForm(false);
      setReviewData({ rating: 5, review_text: '', event_type: '' });
      const r = await api.get(`/vendors/${id}/reviews`);
      setReviews(r.data);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to submit review', 'error'); }
  };

  const heroImage = portfolio[0]?.image_url || (vendor.business_gallery?.[0]) || null;
  const avgRating = Number(vendor.avg_rating || 0);
  const reviewCount = Number(vendor.review_count || 0);

  // Calendar helpers
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

  const getScheduleForDate = (d) => {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return schedule.find(s => s.date?.startsWith(dateStr));
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <nav style={{ background: 'transparent', padding: '16px 0', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', backgroundColor: 'rgba(247,247,248,0.8)' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/vendors" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FiArrowLeft size={16} /> Back to Vendors</Link>
          <Link to="/"><img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '70px' }} /></Link>
        </div>
      </nav>

      <div className="container" style={{ padding: '32px 20px', maxWidth: '800px' }}>
        {/* Hero carousel */}
        <div
          style={{ height: '300px', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px', position: 'relative', background: 'var(--dark)' }}
          onMouseEnter={() => { heroPausedRef.current = true; }}
          onMouseLeave={() => { heroPausedRef.current = false; }}
        >
          {heroMedia.length > 0 ? (
            <>
              {heroMedia.map((item, i) => (
                <div key={i} style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  opacity: i === heroIdx ? 1 : 0,
                  transition: 'opacity 0.6s ease',
                  pointerEvents: i === heroIdx ? 'auto' : 'none'
                }}>
                  <img
                    src={item.image_url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={handleImgError}
                  />
                </div>
              ))}
              {/* Left/Right arrows */}
              {heroMedia.length > 1 && (
                <>
                  <button onClick={() => setHeroIdx(i => i > 0 ? i - 1 : heroMedia.length - 1)} style={{
                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)', color: 'var(--white)', border: 'none', borderRadius: '50%',
                    width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', backdropFilter: 'blur(4px)'
                  }}><FiChevronLeft size={20} /></button>
                  <button onClick={() => setHeroIdx(i => (i + 1) % heroMedia.length)} style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.5)', color: 'var(--white)', border: 'none', borderRadius: '50%',
                    width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', backdropFilter: 'blur(4px)'
                  }}><FiChevronRight size={20} /></button>
                  {/* Dot indicators */}
                  <div style={{ position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px' }}>
                    {heroMedia.map((_, i) => (
                      <button key={i} onClick={() => setHeroIdx(i)} style={{
                        width: i === heroIdx ? '20px' : '8px', height: '8px',
                        borderRadius: '4px', border: 'none',
                        background: i === heroIdx ? 'var(--white)' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer', transition: 'all 0.3s'
                      }} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{ height: '100%', background: 'linear-gradient(135deg, var(--dark), #3a3a3e)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiImage size={40} style={{ color: 'var(--gray-light)', opacity: 0.3 }} />
            </div>
          )}
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <span className="tag tag-gold" style={{ marginBottom: '8px', display: 'inline-block' }}>{vendor.business_category || 'Vendor'}</span>
              <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>{vendor.business_name || vendor.name}</h1>
              <div style={{ display: 'flex', gap: '16px', color: 'var(--gray)', fontSize: '14px', flexWrap: 'wrap' }}>
                {vendor.business_location && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiMapPin /> {vendor.business_location}</span>}
                {vendor.business_price_range && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CoinIcon /> {vendor.business_price_range}</span>}
                {avgRating > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiStar style={{ color: '#F59E0B', fill: '#F59E0B' }} /> {avgRating.toFixed(1)} ({reviewCount} reviews)
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {user && user.role !== 'vendor' && (
                <>
                  <button className="btn btn-primary" onClick={startChat}><FiMessageCircle /> Chat</button>
                  <button className="btn btn-secondary" onClick={() => setShowBooking(true)}><FiMail /> Inquiry</button>
                </>
              )}
              {!user && <Link to="/login" className="btn btn-primary"><FiMessageCircle /> Chat with Vendor</Link>}
            </div>
          </div>
          {vendor.business_description && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--cream-dark)' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>About</h3>
              <p style={{ color: 'var(--gray)', lineHeight: 1.8 }}>{vendor.business_description}</p>
            </div>
          )}
        </div>

        {/* Portfolio */}
        {portfolio.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiImage /> Portfolio ({portfolio.length})</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {portfolio.map((img, i) => (
                <div key={img.id} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', height: '200px', cursor: 'pointer' }} onClick={() => setLightbox(i)}>
                  {img.media_type === 'video' ? (
                    <video src={img.image_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                  ) : (
                    <img src={img.image_url} alt={img.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      onError={handleImgError} />
                  )}
                  {img.media_type === 'video' && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px' }}>▶</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legacy gallery fallback */}
        {portfolio.length === 0 && vendor.business_gallery && vendor.business_gallery.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Portfolio</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {vendor.business_gallery.map((img, i) => (
                <div key={i} style={{ borderRadius: '12px', overflow: 'hidden', height: '200px' }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={handleImgError} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Availability Calendar */}
        {schedule.length > 0 && (
          <div className="card" style={{ marginBottom: '24px', padding: '24px' }}>
            <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiCalendar /> Availability</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <button onClick={() => setCalMonth(new Date(year, month - 1, 1))} style={{ background: 'var(--cream)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiChevronLeft size={16} /></button>
              <span style={{ fontWeight: 600, fontSize: '15px' }}>{calMonth.toLocaleString('en-ZA', { month: 'long', year: 'numeric' })}</span>
              <button onClick={() => setCalMonth(new Date(year, month + 1, 1))} style={{ background: 'var(--cream)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiChevronRight size={16} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '6px' }}>
              {['S','M','T','W','T','F','S'].map((d,i) => (
                <div key={i} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: 'var(--gray)', padding: '6px 0' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
              {calDays.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const sched = getScheduleForDate(d);
                const dateObj = new Date(year, month, d);
                const isPast = dateObj < today;
                const status = sched?.status;
                return (
                  <div key={d} style={{
                    aspectRatio: '1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 500, opacity: isPast ? 0.3 : 1,
                    background: status === 'booked' ? '#FEE2E2' : status === 'available' ? '#DCFCE7' : 'var(--cream)',
                    color: status === 'booked' ? '#DC2626' : status === 'available' ? '#16A34A' : 'var(--dark)',
                  }}>
                    {d}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '12px', color: 'var(--gray)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#DCFCE7' }} /> Available</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#FEE2E2' }} /> Booked</span>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiStar /> Reviews {reviewCount > 0 && `(${reviewCount})`}
            </h3>
            {user && user.role !== 'vendor' && (
              <button className="btn btn-sm btn-secondary" onClick={() => setShowReviewForm(!showReviewForm)}>
                {showReviewForm ? 'Cancel' : 'Write Review'}
              </button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="card" style={{ padding: '24px', marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '16px', fontSize: '15px' }}>Your Review</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px', display: 'block' }}>Rating</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => setReviewData({...reviewData, rating: s})} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                      <FiStar size={28} style={{ color: s <= reviewData.rating ? '#F59E0B' : '#E5E7EB', fill: s <= reviewData.rating ? '#F59E0B' : 'none', transition: 'all 0.15s' }} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Event Type</label>
                <input value={reviewData.event_type} onChange={e => setReviewData({...reviewData, event_type: e.target.value})} placeholder="e.g., Wedding, Birthday" />
              </div>
              <div className="form-group">
                <label>Your Review</label>
                <textarea value={reviewData.review_text} onChange={e => setReviewData({...reviewData, review_text: e.target.value})} rows={3} placeholder="Tell others about your experience..." />
              </div>
              <button className="btn btn-primary" onClick={submitReview} disabled={!reviewData.review_text}><FiSend /> Submit Review</button>
            </div>
          )}

          {/* Rating Summary */}
          {reviewCount > 0 && (
            <div className="card" style={{ padding: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 700, lineHeight: 1 }}>{avgRating.toFixed(1)}</div>
                <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', margin: '6px 0' }}>
                  {[1,2,3,4,5].map(s => (
                    <FiStar key={s} size={14} style={{ color: s <= Math.round(avgRating) ? '#F59E0B' : '#E5E7EB', fill: s <= Math.round(avgRating) ? '#F59E0B' : 'none' }} />
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{reviewCount} reviews</div>
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                {[5,4,3,2,1].map(star => {
                  const count = reviews.filter(r => r.rating === star).length;
                  const pct = reviews.length > 0 ? (count / reviews.length * 100) : 0;
                  return (
                    <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--gray)', width: '10px' }}>{star}</span>
                      <div style={{ flex: 1, height: '6px', background: 'var(--cream-dark)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#F59E0B', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--gray)', width: '18px', textAlign: 'right' }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Review List */}
          {reviews.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray)' }}>
              <p>No reviews yet. Be the first to review this vendor!</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {reviews.map(r => (
                <div key={r.id} className="card" style={{ padding: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{r.reviewer_name}</div>
                      {r.event_type && <span className="tag" style={{ fontSize: '10px' }}>{r.event_type}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1,2,3,4,5].map(s => (
                        <FiStar key={s} size={12} style={{ color: s <= r.rating ? '#F59E0B' : '#E5E7EB', fill: s <= r.rating ? '#F59E0B' : 'none' }} />
                      ))}
                    </div>
                  </div>
                  {r.review_text && <p style={{ color: 'var(--gray)', fontSize: '13px', lineHeight: 1.6 }}>{r.review_text}</p>}
                  <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '8px', opacity: 0.7 }}>{new Date(r.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => setLightbox(null)}>
          {portfolio[lightbox]?.media_type === 'video' ? (
            <video src={portfolio[lightbox]?.image_url} controls autoPlay style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '8px' }} onClick={e => e.stopPropagation()} />
          ) : (
            <img src={portfolio[lightbox]?.image_url} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: '8px' }} onError={handleImgError} />
          )}
          <div style={{ position: 'absolute', bottom: '40px', display: 'flex', gap: '8px' }}>
            {portfolio.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setLightbox(i); }} style={{ width: '10px', height: '10px', borderRadius: '50%', border: 'none', background: i === lightbox ? 'white' : 'rgba(255,255,255,0.3)', cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowBooking(false)}>
          <div className="modal">
            <h2>Send Booking Inquiry</h2>
            <div className="form-group">
              <label>Select Event</label>
              <select value={bookingData.event_id} onChange={e => setBookingData({...bookingData, event_id: e.target.value})}>
                <option value="">Choose an event</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea value={bookingData.message} onChange={e => setBookingData({...bookingData, message: e.target.value})} rows={4} placeholder="Tell the vendor about your event..." />
            </div>
            <div style={{display:'flex',gap:'12px'}}>
              <button className="btn btn-secondary btn-full" onClick={() => setShowBooking(false)}>Cancel</button>
              <button className="btn btn-primary btn-full" onClick={sendInquiry} disabled={!bookingData.event_id}><FiSend /> Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
